import {
  AssetType,
  Placement,
  TransformationTemplate,
} from '@amzn/fire-tv-campaign-automation-service';
import { ValidationProps } from 'src/components/pages/campaignAutomation/input/assets';
import { InputType } from 'src/types';

/**
 * Base configuration for all asset types
 */
interface AssetConfig {
  // Asset file properties
  acceptedFileTypes: string;
  allowedContentTypes: string[];
  globalMaxFileSizeInBytes: number;
  assetKeyPrefix: string;
  fileName: string;

  // Display properties
  dataTestId: string;
  displayLabel: string;
  inputTypeAndTransformationOptions: Partial<Record<InputType, boolean>>;

  // Validation properties
  validationProps: Partial<Record<TransformationTemplate, ValidationProps>>;
}

/**
 * Configuration for asset files (background image, logo, video)
 */
export const ASSET_CONFIG: Record<AssetType, AssetConfig> = {
  [AssetType.BACKGROUND_IMAGE]: {
    // File properties
    acceptedFileTypes: '.jpeg,.jpg,.png',
    allowedContentTypes: ['image/jpeg', 'image/png'],
    globalMaxFileSizeInBytes: 10 * 1024 * 1024,
    assetKeyPrefix: 'bgd_images',
    fileName: 'bgd_image',

    // Display properties
    dataTestId: 'asset-input-background-image',
    displayLabel: 'Background Image',
    inputTypeAndTransformationOptions: {
      [InputType.SEARCH]: true,
      [InputType.UPLOAD]: true,
      [InputType.URL]: true,
    },
    // Validation properties
    validationProps: {
      [TransformationTemplate.NO_TRANSFORMATION]: {
        maxFileSizeInBytes: 450 * 1024,
        dimensionSpecification: {
          minResolution: {
            width: 1920,
            height: 720,
          },
          maxResolution: {
            width: 1920,
            height: 720,
          },
        },
      },
      [TransformationTemplate.DOMINANT_COLOR_FADED_OVERLAY]: {
        maxFileSizeInBytes: 10 * 1024 * 1024,
        dimensionSpecification: {
          aspectRatio: {
            width: 16,
            height: 9,
          },
          minResolution: {
            width: 1280,
            height: 720,
          },
        },
      },
    },
  },

  // Copied as is for backward compatibility
  [AssetType.BACKGROUND_IMAGE_V0]: {
    // File properties
    acceptedFileTypes: '.jpeg,.jpg,.png',
    allowedContentTypes: ['image/jpeg', 'image/png'],
    globalMaxFileSizeInBytes: 10 * 1024 * 1024,
    assetKeyPrefix: 'bgd_images',
    fileName: 'bgd_image',

    // Display properties
    dataTestId: 'asset-input-background-image',
    displayLabel: 'Background Image',
    inputTypeAndTransformationOptions: {
      [InputType.SEARCH]: true,
      [InputType.UPLOAD]: true,
      [InputType.URL]: true,
    },
    // Validation properties
    validationProps: {
      [TransformationTemplate.NO_TRANSFORMATION]: {
        maxFileSizeInBytes: 450 * 1024,
        dimensionSpecification: {
          minResolution: {
            width: 1920,
            height: 720,
          },
          maxResolution: {
            width: 1920,
            height: 720,
          },
        },
      },
      [TransformationTemplate.DOMINANT_COLOR_FADED_OVERLAY]: {
        maxFileSizeInBytes: 10 * 1024 * 1024,
        dimensionSpecification: {
          aspectRatio: {
            width: 16,
            height: 9,
          },
          minResolution: {
            width: 1280,
            height: 720,
          },
        },
      },
    },
  },

  [AssetType.LOGO]: {
    // File properties
    acceptedFileTypes: '.png',
    allowedContentTypes: ['image/png'],
    globalMaxFileSizeInBytes: 10 * 1024 * 1024,
    assetKeyPrefix: 'logo_images',
    fileName: 'logo',

    // Display properties
    dataTestId: 'asset-input-logo',
    displayLabel: 'Logo',
    inputTypeAndTransformationOptions: {
      [InputType.LOGO_TEXT]: true,
      [InputType.UPLOAD]: false,
      [InputType.URL]: false,
    },

    // Validation properties
    validationProps: {
      [TransformationTemplate.NO_TRANSFORMATION]: {
        maxFileSizeInBytes: 450 * 1024,
        dimensionSpecification: {
          minResolution: {
            width: 640,
            height: 260,
          },
          maxResolution: {
            width: 640,
            height: 260,
          },
        },
      },
    },
  },

  [AssetType.VIDEO_TRAILER]: {
    // File properties
    acceptedFileTypes: '.mp4,.mov,.mpeg',
    allowedContentTypes: ['video/mp4', 'video/quicktime', 'video/mpeg'],
    globalMaxFileSizeInBytes: 500 * 1024 * 1024,
    assetKeyPrefix: 'video_trailers',
    fileName: 'video_trailer',

    // Display properties
    dataTestId: 'asset-input-video-trailer',
    displayLabel: 'Video Trailer',
    inputTypeAndTransformationOptions: { [InputType.UPLOAD]: true, [InputType.URL]: true },

    // Validation properties
    validationProps: {
      [TransformationTemplate.NO_TRANSFORMATION]: {
        maxFileSizeInBytes: 10 * 1024 * 1024,
        dimensionSpecification: {
          aspectRatio: {
            width: 16,
            height: 9,
          },
          minResolution: {
            width: 1920,
            height: 1080,
          },
          maxResolution: {
            width: 1920,
            height: 1080,
          },
        },
        videoLengthSpecs: {
          minDurationInSeconds: 5,
          maxDurationInSeconds: 21,
        },
      },
      [TransformationTemplate.CLIPPED_VIDEO]: {
        maxFileSizeInBytes: 500 * 1024 * 1024,
        dimensionSpecification: {
          aspectRatio: {
            width: 16,
            height: 9,
          },
          minResolution: {
            width: 1920,
            height: 1080,
          },
        },
        videoLengthSpecs: {
          minDurationInSeconds: 5,
          maxDurationInSeconds: 120 * 60,
        },
      },
    },
  },
};

export const ASSET_INPUTS: AssetType[] = [
  AssetType.BACKGROUND_IMAGE,
  AssetType.LOGO,
  AssetType.VIDEO_TRAILER,
];

/**
 * Maps placement types to their required asset types
 */
export const PLACEMENT_TO_REQUIRED_ASSETS_CONFIG: Record<Placement, AssetType[]> = {
  [Placement.FEATURE_ROTATOR]: [
    AssetType.BACKGROUND_IMAGE,
    AssetType.LOGO,
    AssetType.VIDEO_TRAILER,
  ],

  /**
   * TODO: Add required assets for`Placement.INLINE_DISPLAY_BANNER`
   * and `Placement.SCREEN_SAVER`
   */
  [Placement.INLINE_DISPLAY_BANNER]: [],
  [Placement.SCREEN_SAVER]: [],
  // For backward compatibility
  [Placement.FEATURE_ROTATOR_V0]: [],
};
